// src/admin/pages/AdminHowToHelp.jsx
import React, { useState, useEffect } from "react";
import { howToHelpService } from "../../../services/api";

const AdminHowToHelp = () => {
  const [howToHelpItems, setHowToHelpItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    heading: "",
    text: "",
  });
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchHowToHelp();
  }, []);

  const ensureFiveEntries = (items) => {
    const existingIds = items.map(item => item.id);
    const requiredIds = [1, 2, 3, 4, 5];
    const missingIds = requiredIds.filter(id => !existingIds.includes(id));
    
    // Create empty entries for missing IDs
    const missingEntries = missingIds.map(id => ({
      id,
      heading: "",
      text: "",
      isEmpty: true // Flag to identify empty entries
    }));
    
    // Combine existing and missing entries, then sort by ID
    const allEntries = [...items, ...missingEntries].sort((a, b) => a.id - b.id);
    
    return allEntries;
  };

  const fetchHowToHelp = async () => {
    try {
      setLoading(true);
      const response = await howToHelpService.getAll();
      // Sort by id to ensure consistent order (1,2,3,4,5)
      const sortedItems = response.data.sort((a, b) => a.id - b.id);
      // Ensure we always have 5 entries
      const itemsWithFive = ensureFiveEntries(sortedItems);
      setHowToHelpItems(itemsWithFive);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching how-to-help items:", err);
      setError("Не вдалося завантажити дані про допомогу");
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleEdit = (item) => {
    setFormData({
      heading: item.heading || "",
      text: item.text || "",
    });
    setEditingId(item.id);

    // Scroll to form
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleCancel = () => {
    setFormData({
      heading: "",
      text: "",
    });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    window.scrollTo(0, 0);

    if (!editingId) return;

    try {
      const currentItem = howToHelpItems.find(item => item.id === editingId);
      
      if (currentItem && currentItem.isEmpty) {
        // Create new entry
        await howToHelpService.create({ ...formData, id: editingId });
        setMessage({ type: "success", text: "Новий пункт успішно створено!" });
      } else {
        // Update existing entry
        await howToHelpService.update(editingId, formData);
        setMessage({ type: "success", text: "Дані успішно оновлено!" });
      }

      handleCancel();
      fetchHowToHelp();

      // Clear message after 3 seconds
      setTimeout(() => {
        setMessage(null);
      }, 3000);
    } catch (err) {
      console.error("Error saving how-to-help item:", err);
      setMessage({
        type: "error",
        text: "Помилка при збереженні даних",
      });
    }
  };

  const formatHeadingPreview = (heading) => {
    if (!heading) return { firstWord: "", restOfHeading: "" };
    
    const words = heading.split(" ");
    const firstWord = words[0] || "";
    const restOfHeading = words.slice(1).join(" ");

    return { firstWord, restOfHeading };
  };

  if (loading) {
    return (
      <div style={styles.pageContainer}>
        <h1 style={styles.pageTitle}>Як допомогти</h1>
        <div style={styles.loadingContainer}>
          <p>Завантаження...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.pageContainer}>
        <h1 style={styles.pageTitle}>Як допомогти</h1>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div style={styles.pageContainer}>
      <h1 style={styles.pageTitle}>Як допомогти</h1>

      {message && (
        <div
          style={{
            ...styles.message,
            backgroundColor:
              message.type === "success"
                ? "#d4edda"
                : message.type === "error"
                ? "#f8d7da"
                : "#fff3cd",
            color:
              message.type === "success"
                ? "#155724"
                : message.type === "error"
                ? "#721c24"
                : "#856404",
          }}
        >
          {message.text}
        </div>
      )}

      {editingId && (
        <div style={styles.formContainer}>
          <h2 style={styles.formTitle}>
            {howToHelpItems.find(item => item.id === editingId)?.isEmpty 
              ? `Створити пункт #${editingId}` 
              : `Редагувати пункт #${editingId}`}
          </h2>

          <form style={styles.form} onSubmit={handleSubmit}>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="heading">
                Заголовок:
              </label>
              <input
                style={styles.input}
                type="text"
                id="heading"
                name="heading"
                value={formData.heading}
                onChange={handleInputChange}
                placeholder="Наприклад: Вставай на захист України поряд з нами"
                required
              />
              <small style={styles.hint}>
                Перше слово буде виділено окремо. Наприклад: "Вставай" + "на захист України поряд з
                нами"
              </small>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="text">
                Опис:
              </label>
              <textarea
                style={styles.textArea}
                id="text"
                name="text"
                value={formData.text}
                onChange={handleInputChange}
                placeholder="Детальний опис того, як можна допомогти..."
                required
              />
            </div>

            <div style={styles.buttonContainer}>
              <button type="button" style={styles.cancelButton} onClick={handleCancel}>
                Скасувати
              </button>
              <button type="submit" style={styles.button}>
                {howToHelpItems.find(item => item.id === editingId)?.isEmpty 
                  ? "Створити пункт" 
                  : "Зберегти зміни"}
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={styles.itemsGrid}>
        {howToHelpItems.map((item, index) => {
          const { firstWord, restOfHeading } = formatHeadingPreview(item.heading);
          const isEmpty = item.isEmpty || (!item.heading && !item.text);

          return (
            <div key={item.id} style={{
              ...styles.itemCard,
              ...(isEmpty ? styles.emptyItemCard : {})
            }}>
              <div style={styles.itemHeader}>
                <span style={styles.itemNumber}>#{item.id}</span>
                <button
                  onClick={() => handleEdit(item)}
                  title={isEmpty ? "Створити" : "Редагувати"}
                  style={{
                    ...styles.editButton,
                    ...(isEmpty ? styles.createButton : {})
                  }}
                >
                  {isEmpty ? "➕ Створити" : "✏️ Редагувати"}
                </button>
              </div>

              <div style={styles.itemContent}>
                {isEmpty ? (
                  <div style={styles.emptyItemContent}>
                    <h3 style={styles.emptyItemHeading}>Пустий пункт</h3>
                    <p style={styles.emptyItemText}>
                      Натисніть "Створити", щоб додати заголовок та опис для цього пункту
                    </p>
                  </div>
                ) : (
                  <>
                    <h3 style={styles.itemHeading}>
                      <span style={styles.firstWord}>{firstWord}</span>{" "}
                      <span style={styles.restOfHeading}>{restOfHeading}</span>
                    </h3>
                    <p style={styles.itemText}>
                      {item.text.length > 150 ? `${item.text.substring(0, 150)}...` : item.text}
                    </p>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  pageContainer: {
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
    padding: "2rem",
  },
  pageTitle: {
    marginBottom: "1.5rem",
    fontSize: "1.8rem",
    color: "var(--secondary-color)",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  message: {
    padding: "1rem",
    marginBottom: "1rem",
    borderRadius: "4px",
  },
  formContainer: {
    marginBottom: "2rem",
    padding: "1.5rem",
    backgroundColor: "#e9e9e9",
    borderRadius: "8px",
  },
  formTitle: {
    marginBottom: "1rem",
    fontSize: "1.3rem",
    color: "var(--secondary-color)",
  },
  form: {
    display: "grid",
    gap: "1rem",
    gridTemplateColumns: "minmax(0, 1fr)",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    marginBottom: "0.5rem",
    fontWeight: "600",
  },
  input: {
    padding: "0.8rem",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontFamily: '"UAFSans", sans-serif',
  },
  textArea: {
    padding: "0.8rem",
    border: "1px solid #ddd",
    borderRadius: "4px",
    fontFamily: '"UAFSans", sans-serif',
    minHeight: "120px",
    resize: "vertical",
  },
  hint: {
    marginTop: "0.25rem",
    fontSize: "0.85rem",
    color: "#666",
    fontStyle: "italic",
  },
  button: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "green",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: '"UAFSans", sans-serif',
    fontWeight: "500",
    cursor: "pointer",
    transition: "background-color 0.3s",
  },
  cancelButton: {
    padding: "0.8rem 1.5rem",
    backgroundColor: "#FFBB00",
    color: "white",
    border: "none",
    borderRadius: "4px",
    fontFamily: '"UAFSans", sans-serif',
    fontWeight: "500",
    cursor: "pointer",
    transition: "background-color 0.3s",
    marginRight: "1rem",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    gridColumn: "1 / -1",
  },
  itemsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
    gap: "1.5rem",
    marginTop: "1rem",
  },
  itemCard: {
    border: "1px solid #eee",
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 2px 5px rgba(0, 0, 0, 0.05)",
    backgroundColor: "white",
  },
  emptyItemCard: {
    border: "2px dashed #ddd",
    backgroundColor: "#fafafa",
  },
  itemHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "1rem",
    backgroundColor: "#f8f9fa",
    borderBottom: "1px solid #eee",
  },
  itemNumber: {
    fontSize: "1rem",
    fontWeight: "600",
    color: "var(--primary-color)",
  },
  editButton: {
    backgroundColor: "var(--primary-color)",
    color: "black",
    border: "none",
    borderRadius: "4px",
    padding: "0.5rem 1rem",
    cursor: "pointer",
    fontSize: "0.9rem",
    fontFamily: '"UAFSans", sans-serif',
    transition: "background-color 0.3s",
  },
  createButton: {
    backgroundColor: "#28a745",
    color:"white",
  },
  itemContent: {
    padding: "1rem",
  },
  itemHeading: {
    margin: "0 0 1rem",
    fontSize: "1.1rem",
    lineHeight: "1.4",
  },
  firstWord: {
    color: "var(--primary-color)",
    fontWeight: "700",
  },
  restOfHeading: {
    color: "var(--secondary-color)",
    fontWeight: "600",
  },
  itemText: {
    margin: "0",
    color: "#666",
    fontSize: "0.9rem",
    lineHeight: "1.5",
    whiteSpace: "pre-line",
  },
  emptyItemContent: {
    textAlign: "center",
    padding: "1rem 0",
  },
  emptyItemHeading: {
    margin: "0 0 0.5rem",
    fontSize: "1rem",
    color: "#999",
    fontStyle: "italic",
  },
  emptyItemText: {
    margin: "0",
    color: "#999",
    fontSize: "0.85rem",
    fontStyle: "italic",
    lineHeight: "1.4",
  },
  emptyState: {
    textAlign: "center",
    padding: "3rem",
    backgroundColor: "#e9e9e9",
    borderRadius: "8px",
    marginTop: "1rem",
  },
};

export default AdminHowToHelp;